// useLeagueSchedule -- the Home schedule card's slate for a WEEKLY league (#1082).
// Single job: one non-realtime read of game_cache for `league` over its rolling
// scheduleWindow, refreshed every 60s while mounted. Reads game_cache; writes
// nothing. Explicitly NOT a widening of useGameCacheRealtime: that hook's 3-day
// window feeds the live-slip strip, the rail, myGames and settlement display, and
// a week of UPCOMING NFL games does not belong in any of those. A daily league
// returns null immediately (the realtime feed already has today's games); a
// failed read is a warning (the interval retries -- CC-IMPL-037) and returns
// null so the card falls back to the realtime prop.
import { useEffect, useState } from "react";
import { captureMessage } from "../lib/errorReporting";
import { supabase } from "../lib/supabase";
import { transformRow } from "./useGameCacheRealtime";
import { scheduleCadence, scheduleWindow } from "../lib/league-schedule";

const REFRESH_MS = 60 * 1000;

export function useLeagueSchedule(league) {
  const weekly = scheduleCadence(league) === "weekly";
  const [games, setGames] = useState(null);
  // true from mount / league switch until the FIRST read settles (success or error),
  // so the card can show a skeleton instead of the 3-day fallback for one round-trip.
  // The 60s refresh never flips it back on -- no flicker on refresh.
  const [loading, setLoading] = useState(weekly);

  useEffect(() => {
    if (!weekly || !supabase) { setGames(null); setLoading(false); return undefined; }
    let cancelled = false;

    // One warning per failed tick (CC-IMPL-037: the interval is the retry); the
    // page falls back to the realtime prop while games is null.
    const warn = (message, extra) => captureMessage(`useLeagueSchedule read failed: ${message}`, {
      level: "warning",
      tags: { function: "useLeagueSchedule", league },
      extra,
    });

    const load = async () => {
      const { from, to } = scheduleWindow(league);
      try {
        const { data, error } = await supabase
          .from("game_cache")
          .select("*")
          .eq("league", league)
          .gte("game_date", from)
          .lte("game_date", to)
          .order("game_start_time", { ascending: true });
        if (cancelled) return;
        if (error) {
          warn(error.message || "unknown", { code: error.code, from, to });
          setGames(null);
          return;
        }
        setGames((data || []).map(transformRow).filter(Boolean));
      } catch (err) {
        // postgrest-js returns fetch failures as { error }; this is the belt for a
        // future throw inside the map -- never an unhandled rejection off a timer.
        if (cancelled) return;
        warn(err?.message || "thrown", { thrown: true, from, to });
        setGames(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setGames(null);
    setLoading(true);
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [league, weekly]);

  return { games: weekly ? games : null, loading: weekly && loading };
}
